/**
 * Canonical date/timezone formatting (audit p1.4).
 *
 * ~96 files format dates inline and ~30 hardcode an `America/...` timezone. This
 * is the single source; default zone is America/Los_Angeles (Central Oregon).
 * The `ci:date-format` gate ratchets new inline date formatting toward zero.
 */
const TZ = 'America/Los_Angeles'
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
/**
 * An already-printed calendar day ("Oct 22, 2026", "Oct. 22, 2026"). `new
 * Date('Oct 22, 2026')` is midnight in the server's zone (UTC on Vercel),
 * which is the prior day in Pacific, so a label passed back in as an `asOf`
 * printed one day early (Public Look 2026-10-23: La Pine supply "Oct 21"
 * beside "at a glance" "Oct 22", both from one stamp).
 */
const PRINTED_DAY = /^[A-Z][a-z]{2,8}\.? \d{1,2}, \d{4}$/

/** Format a date in the brand timezone. Defaults to "Jun 22, 2026"; override via opts. */
export function formatDate(
  d: string | number | Date | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (d == null) return '—'
  // MLS CloseDate and other calendar days are YYYY-MM-DD with no clock.
  // `new Date('2026-06-25')` is UTC midnight, which is the prior day in Pacific.
  const date =
    typeof d === 'string' && DATE_ONLY.test(d.trim())
      ? new Date(`${d.trim()}T12:00:00.000Z`)
      : typeof d === 'string' && PRINTED_DAY.test(d.trim())
        ? new Date(`${d.trim().replace('.', '')} 12:00:00 GMT`)
        : d instanceof Date
        ? d
        : new Date(d)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...opts,
  }).format(date)
}

/**
 * A date range in the brand timezone. When both ends land on the same printed
 * day it collapses to that one day, never "Oct 23, 2026 to Oct 23, 2026"
 * (Public Look 2026-10-23). `joiner` is the word between two distinct ends:
 * "to" in plain copy, "through" inside the ODS "for the period of" notice.
 * Returns '' when either end is missing or unreadable.
 */
export function formatDateSpan(
  start: string | number | Date | null | undefined,
  end: string | number | Date | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
  joiner: 'to' | 'through' = 'to',
): string {
  const a = formatDate(start, opts)
  const b = formatDate(end, opts)
  if (a === '—' || b === '—') return ''
  return a === b ? a : `${a} ${joiner} ${b}`
}

/** Date + time in the brand timezone, e.g. "Jun 22, 2026, 3:04 PM". */
export function formatDateTime(d: string | number | Date | null | undefined): string {
  return formatDate(d, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * Format a YYYY-MM-DD calendar day without treating it as UTC midnight.
 * `new Date('2026-08-18')` is 00:00Z, which is the prior Pacific evening.
 * Noon UTC keeps the civil day the same in America/Los_Angeles.
 */
export function formatCalendarDay(
  ymd: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (ymd == null) return ''
  const trimmed = String(ymd).trim()
  if (!trimmed) return ''
  if (!DATE_ONLY.test(trimmed)) {
    const formatted = formatDate(trimmed, opts)
    return formatted === '—' ? '' : formatted
  }
  return formatDate(`${trimmed}T12:00:00Z`, opts)
}

/**
 * "2023-08-26" reads as "August 2023". The day a measurement window opened is
 * noise beside the month, and a window stated to the day invites a precision
 * the window does not have.
 *
 * Returns the empty string for anything that is not a calendar day, which is
 * what the place-character sentences branch on: with no month to name, the
 * sentence drops its "since ..." clause rather than printing a lone dash.
 */
export function formatMonthYear(ymd: string | null | undefined): string {
  if (ymd == null) return ''
  const trimmed = String(ymd).trim()
  if (!DATE_ONLY.test(trimmed)) return ''
  return formatCalendarDay(trimmed, { month: 'long', day: undefined })
}

/**
 * YYYY-MM-DD calendar-day key for an instant in the brand timezone. Used by the
 * §09 Tasks/Calendar surfaces to group true-instant timestamps (crm_tasks.due_at)
 * into Pacific calendar days. Lives here so Intl stays inside lib/format
 * (ci:date-format).
 */
export function zonedDateKey(d: string | number | Date, timeZone: string = TZ): string {
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return ''
  // en-CA yields YYYY-MM-DD directly.
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(date)
}

/** Minutes since midnight for an instant in the brand timezone (grid placement). */
export function zonedMinutes(d: string | number | Date, timeZone: string = TZ): number {
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return 0
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  return (get('hour') % 24) * 60 + get('minute')
}

/**
 * Local short weekday ("Mon".."Sun") + minutes since midnight for an instant in
 * an IANA zone. Used by the office-hours evaluator (lib/crm/office-hours.ts) —
 * a timezone CALCULATION, kept here so every Intl.DateTimeFormat call lives in
 * the canonical module (ci:date-format).
 */
export function zonedDayMinutes(now: Date, timeZone: string): { day: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || TZ,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const day = get('weekday') || 'Mon'
  // hour12:false can yield "24" for midnight in some ICU versions — normalize.
  const hour = Number(get('hour')) % 24
  const minute = Number(get('minute'))
  return { day, minutes: hour * 60 + minute }
}

/**
 * ISO 8601 for a structured-data date (JSON-LD datePosted / dateModified), or
 * null when the value is absent or unparseable. One helper for every schema
 * builder, so a date-only feed value and a timestamp normalize the same way.
 */
export function toIsoTimestamp(value: string | number | Date | null | undefined): string | null {
  if (value == null || value === '') return null
  const t = value instanceof Date ? value : new Date(value)
  return Number.isNaN(t.getTime()) ? null : t.toISOString()
}

/**
 * "9:43pm EDT": the clock at an instant in an IANA zone, with the zone's short
 * name. For copy that names someone else's local time, such as the SMS
 * quiet-hours refusal for a number in another zone (lib/comms/guards.ts).
 */
export function formatZonedClock(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  }).formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('hour')}:${get('minute')}${get('dayPeriod').toLowerCase()} ${get('timeZoneName')}`.trim()
}
